/**
 * Formatting.
 *
 * Everything the office sees is rendered in Europe/Rome; everything stored is UTC.
 * Durations come from the server already computed in UTC, so the DST edge case is
 * handled once, there — see hh-backend/src/domain/shifts.ts.
 */

import { formatDistanceToNowStrict } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { arSA, enGB, it } from 'date-fns/locale';

import type { Locale } from '@/i18n';

/*
 * Mutable on purpose, and set by the i18n provider rather than read from a hook.
 *
 * These are plain functions called from dozens of render bodies and from click handlers
 * outside React entirely, so threading a locale through every call site would touch the
 * whole portal to say one thing. The provider writes this whenever the language changes,
 * which happens in the same tick as the state update that re-renders everything — so by
 * the time anything formats a date, the locale is already right.
 *
 * enGB rather than enUS: this is an Italian company, and "21 Sep 2026" matches the
 * day-month order the office already reads.
 */
let locale = { locale: it };

/** Called by the i18n provider. Not part of the public formatting API. */
export function setDateLocale(next: Locale): void {
  locale = { locale: next === 'en' ? enGB : next === 'ar' ? arSA : it };
}

/**
 * The tag to hand to `Intl` for a portal language. Arabic is pinned to Latin digits and
 * the Gregorian calendar: bare "ar" gives Arabic-Indic digits in some browsers and the
 * Hijri calendar for ar-SA, and the portal shows Western digits and Gregorian dates in
 * every language so a time or a date reads the same on every screen and in the exports.
 */
export function intlTag(next: Locale): string {
  if (next === 'ar') return 'ar-u-nu-latn-ca-gregory';
  return next === 'en' ? 'en-GB' : 'it-IT';
}
export const TIMEZONE = import.meta.env.VITE_APP_TIMEZONE ?? 'Europe/Rome';

const d = (iso: string | Date) => (iso instanceof Date ? iso : new Date(iso));

/*
 * In Milan's time, whatever the computer's own clock is set to. This said "rendered in
 * Europe/Rome" for a long time while actually using the browser's zone — the same
 * thing in the office, two hours out for anyone looking from elsewhere, and different
 * from the payroll export and the app.
 */
const fmt = (iso: string | Date, pattern: string, withLocale = true) =>
  formatInTimeZone(d(iso), TIMEZONE, pattern, withLocale ? locale : undefined);

export const time = (iso: string | Date) => fmt(iso, 'HH:mm', false);
export const dateShort = (iso: string | Date) => fmt(iso, 'd MMM yyyy');
export const dateLong = (iso: string | Date) => fmt(iso, 'EEEE d MMMM yyyy');
export const dateTime = (iso: string | Date) => fmt(iso, 'd MMM yyyy, HH:mm');
export const ago = (iso: string | Date) =>
  formatDistanceToNowStrict(d(iso), { addSuffix: true, ...locale });

/**
 * A wall-clock time typed in the portal ("2026-09-30T09:51" from a datetime-local, or a
 * "2026-09-30T00:00:00" day boundary) — meant in Milan — as a UTC instant for the API.
 * `new Date(value)` would read it in the computer's own zone instead.
 */
export const fromMilan = (localValue: string) => fromZonedTime(localValue, TIMEZONE).toISOString();

/** An instant, as the value a datetime-local input shows for it in Milan. */
export const toMilanInput = (iso: string | Date) => fmt(iso, "yyyy-MM-dd'T'HH:mm", false);

/** `7h 20m` */
export function duration(minutes: number | null): string {
  if (minutes === null) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Decimal hours, for payroll exports. `7.33` */
export function decimalHours(minutes: number | null): string {
  if (minutes === null) return '';
  return (minutes / 60).toFixed(2);
}

export function distance(metres: number | null): string {
  if (metres === null) return '—';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1).replace('.', ',')} km`;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export const fullName = (u: { firstName: string; lastName: string }) =>
  `${u.firstName} ${u.lastName}`;

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();
}

/**
 * ISO date (yyyy-MM-dd) — the day in Milan. Used for "today" in queries and for date
 * inputs. A bare "yyyy-MM-dd" string is already a calendar day and comes back as is:
 * shifting it through a timezone could move it to the day before.
 */
export const isoDate = (iso: string | Date) =>
  typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : fmt(iso, 'yyyy-MM-dd', false);
